import { v4 as UUIDv4 } from 'uuid';
import clone from 'lodash/clone';
import omit from 'lodash/omit';
import { translate } from '@shakkerai/util-i18n';
import { getImageOssPath, getRemoteImageSize } from '@shakkerai/util-image';
import { notifyError } from '@shakkerai/pro-components';
import { getVipState } from '@shakkerai/pro-store';
import { getTempState, getProjectState } from '@starflow/app/store';
import { MODEL_SD35M, presetCheckpoints } from '@shakkerai/pro-models';
import { MODEL_FLUX, SD3_MODELS_FAMILY, MODEL_SD35 } from '@shakkerai/pro-models';
import {
  inpaintPresetModelsMap,
  presetCheckpointsMap,
  MODEL_SD15,
  inpaintCustomModelParam,
} from '@shakkerai/pro-models';
import { defaultInpaintModelKey, fallbackModelBaseParams, MODEL_SDXL } from '@shakkerai/pro-models';
import { generateMask, compressRemoteImage, isOverseas } from '@shakkerai/pro-utils';
import { pickModelsFromSouceElement } from '../../generator/helpers/generate';
import { findBestSizeByReferenceSize, copyOSSImage, MAX_UPLOAD_SIZE } from '@shakkerai/pro-utils';
import { defaultHiResScale, samplerMethodOptions, presetNegativePrompts } from '@shakkerai/pro-constants';
import {
  controlNetInitialValues,
  controlNetConfig,
  processorSubParams,
  FAKE_IMAGE_URL,
} from '@shakkerai/pro-constants';
import { facialReferenceImages, facialReferenceDefaultValues } from '@shakkerai/pro-constants';
import { calcPreprocessorResolution, mapSD3Params } from '../../generator/helpers/generate';
import { fixLoraWeight, baseTypeToNumber } from '../../models/helpers';
import { applyDerivativeResults } from '../../derivative';
import { getUnitData, updateUnitData } from '../../../helpers/flow';

const INPAINTING_MAX_SIZE = 2048;

export const inpaintParamsHandler = async ({ params, isTryClacMode = false }) => {
  let enabledControlNets = [];

  const { sourceElement } = params;

  if (!sourceElement || (sourceElement.type !== 'image' && !isTryClacMode)) return;

  const inpaintParams = params.segments || {};

  if (!inpaintParams.selections?.length && !isTryClacMode) return;

  const isSimpleMode = getTempState('globalMode') === 'simple';

  const promptSettingsParams = getTempState('promptSettingsParams');

  if (isSimpleMode) {
    params.controlnets = [];
  }

  if (params.modelPreset === 'original') {
    // 原图风格
    const models = await pickModelsFromSouceElement(sourceElement);
    params = Object.assign({}, params, models, { modelPreset: 'original' });
  }

  let baseModel = params.checkpoint;
  // 如果选择的预设模型，checkpoint为null，如果loras也没有，则去预设的sdxl模型
  let paramsBaseType = MODEL_SDXL;

  if (!baseModel && !params.presetBaseModelId && params.loraModels?.length < 1) {
    paramsBaseType = MODEL_SDXL;
  } else {
    paramsBaseType = baseTypeToNumber(baseModel?.baseType || params.baseType || MODEL_SDXL);
  }

  if (params.modelPreset !== 'custom' && params.modelPreset !== 'original') {
    paramsBaseType = MODEL_SDXL;
  }
  const nodeId = getProjectState('currentNodeId');
  const currentUnitdata = getUnitData(nodeId);
  if (params.modelPreset === 'custom' || params.modelPreset === 'original') {
    // 如果是预设的模型，需要把预设的参数塞进来
    if (params.presetBaseModelId && presetCheckpointsMap[params.presetBaseModelId]) {
      baseModel = presetCheckpointsMap[params.presetBaseModelId].versionDetail;
      paramsBaseType = baseTypeToNumber(presetCheckpointsMap[params.presetBaseModelId].versionDetail.baseType);
      if (currentUnitdata.didMount && paramsBaseType === MODEL_FLUX) {
        updateUnitData(nodeId, {
          didMount: false,
          cfgScale: 30,
        });
      }
    }
  }

  const isSD3FamilyModel = SD3_MODELS_FAMILY[paramsBaseType];
  const isFluxModel = paramsBaseType === MODEL_FLUX;
  const isSD35Model = paramsBaseType === MODEL_SD35 || paramsBaseType === MODEL_SD35M;
  const isGenMode = !isTryClacMode; // 是生成模式
  // 如果是不是预设模型 且basetype为19或21要把这些参数带进来

  if (!params.presetBaseModelId && (isFluxModel || isSD35Model)) {
    Object.assign(baseModel, { ...inpaintCustomModelParam });
    if (currentUnitdata.didMount) {
      updateUnitData(nodeId, {
        didMount: false,
        cfgScale: 30,
      });
    }
  }
  if (!isSD3FamilyModel || isFluxModel) {
    enabledControlNets = params.controlnets?.filter((item) => item.type && item.sourceImage?.url) || [];
  }

  let referenceImage = sourceElement;
  let tempReferenceImagePath = null;
  let withFaceCN = false;

  if (isGenMode && referenceImage && referenceImage.url) {
    const remoteSize = await getRemoteImageSize(referenceImage.url);

    if (remoteSize.width > MAX_UPLOAD_SIZE || remoteSize.height > MAX_UPLOAD_SIZE) {
      try {
        const compressUrl = await compressRemoteImage(referenceImage.url, MAX_UPLOAD_SIZE, MAX_UPLOAD_SIZE, true);
        referenceImage = {
          ...referenceImage,
          isTemp: true,
          width: MAX_UPLOAD_SIZE,
          height: MAX_UPLOAD_SIZE,
          url: compressUrl,
        };
      } catch (error) {
        console.log(error);
      }
    }
  }

  if (isGenMode) {
    if (referenceImage.isTemp) {
      tempReferenceImagePath = getImageOssPath(referenceImage.url);
    } else {
      tempReferenceImagePath = await copyOSSImage(
        getImageOssPath(referenceImage.url),
        true,
        isOverseas ? undefined : '3',
      )
        .then((res) => res.url)
        .then(getImageOssPath);
    }
    // 换脸图片操作
  } else {
    // 预估算力消耗会走到这里，用一个固定的参图片路径就行
    tempReferenceImagePath = 'copy/sd-images/2daf83d29cec1eacbaa1545a110179a180612c93bff7d9f1f150e62001a87f5e.png';
  }

  const baseParams = {
    adetailerEnable: 0,
    mode: 1,
    uuid: UUIDv4(),
    projectData: clone(omit(params, ['sourceElement'])),
    vae: '',
    taskQueuePriority: getVipState('vipAccelerate') ? 2 : 1, // 1 普通队列 2加速队列
  };

  baseParams.projectData.mode = 1;
  baseParams.projectData.isSimpleMode = isSimpleMode;
  baseParams.projectData.baseType = paramsBaseType;

  if (currentUnitdata.baseType !== paramsBaseType) {
    updateUnitData(nodeId, {
      baseType: paramsBaseType,
    });
  }
  if (baseParams.projectData.checkpoint?.remark) {
    baseParams.projectData.checkpoint = { ...baseParams.projectData.checkpoint, remark: '' };
  }

  if (Array.isArray(baseParams.projectData.loraModels)) {
    baseParams.projectData.loraModels = baseParams.projectData.loraModels.map((item) => {
      return { ...item, remark: '' };
    });
  }

  let prompt = params.prompt?.trim() || '';
  let negativePrompt = params.negativePrompt?.trim() || '';

  // 去除提示词中的引号、大于号、小于号
  prompt = prompt?.replace(/['"<>]/g, '') || '';
  negativePrompt = negativePrompt?.replace(/['"<>]/g, '') || '';

  baseParams.projectData.prompt = prompt;
  baseParams.projectData.negativePrompt = negativePrompt;

  // 核心生图参数，传给txt2img或者img2img的
  const generateParams = {
    width: params.width,
    height: params.height,
    prompt: prompt,
    negativePrompt: negativePrompt,
    samplingMethod: params.samplerMethod || '15',
    samplingStep: isSimpleMode ? 30 : params.samplingSteps || 20,
    imgCount: params.count,
    batchSize: 1,
    batchCount: 1,
    cfgScale: params.cfgScale,
    clipSkip: params.clipSkip,
    seed: -1,
    tiling: 0,
    seedExtra: 0,
    restoreFaces: 0,
    hiResFix: 0,
    extraNetwork: [],
  };

  Object.assign(baseParams, {
    checkpointId: baseModel?.versionIdInpaint || baseModel?.versionId,
    additionalNetwork: params.loraModels
      ?.filter((lora) => baseTypeToNumber(lora.baseType) === paramsBaseType)
      .map((lora) => ({
        modelId: lora?.versionId,
        weight: fixLoraWeight(Number(Number(lora.weight || 0.8).toFixed(2))),
        type: 0,
      })),
  });

  if (isSD3FamilyModel) {
    generateParams.checkPointName = baseParams.checkpointId;
  }

  let maskImageId = null;

  if (isGenMode) {
    console.log(`[DEBUG] segments`, inpaintParams);
    maskImageId = await generateMask(sourceElement, { fixPoints: true, segments: inpaintParams }).then(getImageOssPath);
  } else {
    // 预估算力消耗会走到这里，用一个固定的mask图片路径就行
    maskImageId = 'copy/sd-images/2daf83d29cec1eacbaa1545a110179a180612c93bff7d9f1f150e62001a87f5e.png';
  }

  baseParams.projectData.generateType = 'inpaint';
  baseParams.projectData.sourceElement = {
    id: UUIDv4(),
    sourceId: sourceElement.id,
    innerTaskId: sourceElement.innerTaskId,
    innerImageId: sourceElement.innerImageId,
    width: Math.ceil(sourceElement.width * sourceElement.scaleX),
    height: Math.ceil(sourceElement.height * sourceElement.scaleY),
    url: sourceElement.url,
  };

  // 重绘的尺寸就是原图的尺寸
  generateParams.width = Math.ceil(sourceElement.width * sourceElement.scaleX);
  generateParams.height = Math.ceil(sourceElement.height * sourceElement.scaleY);

  if (generateParams.width > INPAINTING_MAX_SIZE || generateParams.height > INPAINTING_MAX_SIZE) {
    notifyError(translate('inpaint_size_too_large'));
    return;
  }

  const { width, height } = findBestSizeByReferenceSize(generateParams.width, generateParams.height, 2048);

  generateParams.width = width;
  generateParams.height = height;

  generateParams.imageMode = 4;
  generateParams.inpaintArea = 0;
  generateParams.maskBlur = 4;
  generateParams.maskMode = 0;
  generateParams.maskPadding = 32;
  generateParams.maskedContent = 1;

  // generateParams.denoisingStrength = params.strength / 100;
  // generateParams.sourceImageId = tempReferenceImagePath;

  let brushnetModelId = null;

  if (isFluxModel) {
    brushnetModelId = 2804415; // 2590646(alpha)
  } else if (isOverseas) {
    brushnetModelId = paramsBaseType === MODEL_SD15 ? 1506050 : 1506052;
  } else {
    brushnetModelId = paramsBaseType === MODEL_SD15 ? 2247834 : 2247833;
  }

  const brushNetConfig = {
    width,
    height,
    sourceImageId: tempReferenceImagePath,
    maskImageId,
    controlMode: 0,
    resizeMode: 1,
    pixelErfect: 1,
    pixelPerfect: 1,
    startingControlStep: 0,
    showMask: true,
    preprocessor: 40,
    lockModel: true,
    model: brushnetModelId,
    annotationParameters: { inpaint: {} },
  };

  if (isFluxModel) {
    brushNetConfig.controlWeight = 0.4 + (1 - params.strength / 100) * 0.3;
    brushNetConfig.endingControlStep = 0.7;
    brushNetConfig.preprocessor = 41;
  } else {
    brushNetConfig.controlWeight = 1;
    brushNetConfig.endingControlStep = Math.min(0.8 + (params.strength / 100) * 0.2, 1);
  }

  enabledControlNets.push(brushNetConfig);

  generateParams.resizeScale = 1;
  generateParams.resizeMode = 1;

  if (params.hires) {
    generateParams.resizeWidth = generateParams.width * defaultHiResScale;
    generateParams.resizeHeight = generateParams.height * defaultHiResScale;
  } else {
    generateParams.resizeWidth = generateParams.width;
    generateParams.resizeHeight = generateParams.height;
  }

  // 低门槛模式的话，使用通用+崩手预设，否则使用用户选择的预设
  const pickedPresetNegativePrompts = isSimpleMode ? ['common', 'bad_hand'] : params.presetNegativePrompts;
  if (paramsBaseType === MODEL_SD15) {
    generateParams.negativePrompt = [generateParams.negativePrompt]
      .concat(pickedPresetNegativePrompts.map((item) => presetNegativePrompts[item].text))
      .filter((item) => item)
      .join(',');

    if (!isSD3FamilyModel) {
      baseParams.vae = params.vae && params.vae !== '-1' ? Number(params.vae) : '';
    }
  }

  generateParams.promptRecommend = false;

  if (params.modelPreset !== 'custom' && params.modelPreset !== 'original') {
    const presetModel = inpaintPresetModelsMap[params.modelPreset || defaultInpaintModelKey];
    baseParams.checkpointId = presetModel?.value;
  }

  // 提示词处理
  // const originalPrompt = generateParams.prompt;
  generateParams.prompt = generateParams.prompt?.replace(/^,+/, '') || '';
  generateParams.negativePrompt = generateParams.negativePrompt?.replace(/^,+/, '') || '';

  if (params.presetBaseModelId) {
    const baseTypeNgPrompt = fallbackModelBaseParams[paramsBaseType]?.ngPrompt;
    if (baseTypeNgPrompt) {
      generateParams.negativePrompt += ',' + baseTypeNgPrompt;
    }
    const presetModel = presetCheckpoints.find((preM) => preM.id === params.presetBaseModelId);
    params.presetBaseModel = [presetModel];
  }

  const loraNgPrompts = params.loraModels
    ?.filter((lora) => !!lora.ngPrompt)
    ?.map((lora) => lora.ngPrompt)
    ?.join(',');

  if (loraNgPrompts) {
    generateParams.negativePrompt += ',' + loraNgPrompts;
  }

  if (baseModel?.ngPrompt) {
    generateParams.negativePrompt += ',' + baseModel.ngPrompt;
  }

  const loraTriggerWords = params.loraModels
    ?.filter((lora) => !!lora.triggerWord)
    ?.map((lora) => lora.triggerWord)
    ?.join(',');

  if (loraTriggerWords) {
    generateParams.prompt = loraTriggerWords + ',' + generateParams.prompt;
  }

  if (baseModel?.triggerWord) {
    generateParams.prompt = baseModel.triggerWord + ',' + generateParams.prompt;
  }

  if (params.seedType === '0' || isSimpleMode) {
    // 简单模式下也强制使用随机种子
    generateParams.seed = -1;
  } else {
    generateParams.seed = params.seedNumber ? Number(params.seedNumber) : -1;
  }

  generateParams.width = Math.round(generateParams.width);
  generateParams.height = Math.round(generateParams.height);

  // 增加提示词修饰
  baseParams.projectData.promptRecommend = !!promptSettingsParams?.prompt_setting_enhance;

  baseParams.projectData.renderWidth = generateParams.width;
  baseParams.projectData.renderHeight = generateParams.height;

  // 增加controlnet参数
  if (enabledControlNets.length) {
    let index = 0;
    baseParams.controlNet = [];

    for (const item of enabledControlNets) {
      let tempSourceImagePath = item.sourceImageId;

      if (!tempSourceImagePath && isGenMode) {
        tempSourceImagePath = await copyOSSImage(
          getImageOssPath(item.sourceImage.url),
          true,
          isOverseas ? undefined : '3',
        )
          .then((res) => res.url)
          .then(getImageOssPath);
      }

      // 算力计算情况
      if (!tempReferenceImagePath && !isGenMode) {
        tempReferenceImagePath = FAKE_IMAGE_URL;
      }

      // 算力计算情况
      if (!tempSourceImagePath && !isGenMode) {
        tempSourceImagePath = FAKE_IMAGE_URL;
      }

      const cnDefaultConfig = controlNetConfig[item.type]?.[paramsBaseType];

      let model = item.model || cnDefaultConfig?.models?.[0];
      let preprocessor = item.preprocessor || cnDefaultConfig?.preprocessors?.[0] || 0;

      if (isSimpleMode && !item.lockModel) {
        // 简单模式下局部重绘使用默认值
        model = controlNetConfig[item.type]?.[MODEL_SDXL]?.models?.[0];
        preprocessor = controlNetConfig[item.type]?.[MODEL_SDXL]?.preprocessors?.[0] || 0;
      }

      let sourceSize = item.sourceImage;

      if (!sourceSize?.width && item.width) {
        sourceSize = {
          width: item.width,
          height: item.height,
        };
      }

      const pixelPerfect = !sourceSize ? 0 : typeof item.pixelPerfect !== 'undefined' ? Number(item.pixelPerfect) : 1;
      const preprocessorResolution = pixelPerfect ? calcPreprocessorResolution(sourceSize, generateParams) : 0;

      const annotationParameters = processorSubParams[preprocessor]
        ? {
            [processorSubParams[preprocessor].name]: Object.fromEntries(
              processorSubParams[preprocessor].items.map((key) => {
                if (key === 'preprocessorResolution' && pixelPerfect) {
                  return [key, preprocessorResolution];
                }

                return [key, item[key] || controlNetInitialValues[key]];
              }),
            ),
          }
        : item.annotationParameters || {};

      if (item.type === 'face') {
        if (!item.sourceImage?.url) {
          continue;
        }

        let tempReferenceImagePath = null;
        let referenceImageUrl = null;
        let referenceImageSize = null;

        const { referenceType, faceType, bodyType, referenceImage } = item;

        if (referenceType === 'preset') {
          const key = `${bodyType}_${faceType}`;

          if (facialReferenceImages[key]) {
            referenceImageUrl = facialReferenceImages[key][isOverseas ? 'shkUrl' : 'picUrl'];
            referenceImageSize = facialReferenceImages[key];
          }
        } else if (referenceType === 'same') {
          tempReferenceImagePath = tempSourceImagePath;
          referenceImageSize = sourceSize;
        } else if (referenceType === 'custom') {
          referenceImageUrl = referenceImage.url;
          referenceImageSize = referenceImage;
        }

        if (!referenceImageUrl && !tempReferenceImagePath) {
          continue;
        }

        if (!tempReferenceImagePath && isGenMode) {
          tempReferenceImagePath = await copyOSSImage(
            getImageOssPath(referenceImageUrl),
            true,
            isOverseas ? undefined : '3',
          )
            .then((res) => res.url)
            .then(getImageOssPath);
        }

        // 算力计算情况
        if (!tempReferenceImagePath && !isGenMode) {
          tempReferenceImagePath = FAKE_IMAGE_URL;
        }

        if (!tempSourceImagePath && !isGenMode) {
          tempSourceImagePath = FAKE_IMAGE_URL;
        }

        withFaceCN = true;

        baseParams.controlNet.push(
          {
            allowPreview: 1,
            unitOrder: ++index,
            width: sourceSize.width,
            height: sourceSize.height,
            annotationParameters: { instantIdFaceEmbedding: { preprocessorResolution } },
            controlMode: 0,
            controlWeight: +(
              item['faceEmbeding.controlWeight'] || facialReferenceDefaultValues['faceEmbeding.controlWeight']
            ),
            resizeMode: +(item['faceEmbeding.resizeMode'] || facialReferenceDefaultValues['faceEmbeding.resizeMode']),
            startingControlStep: +(
              item['faceEmbeding.startingControlStep'] ||
              facialReferenceDefaultValues['faceEmbeding.startingControlStep']
            ),
            endingControlStep: +(
              item['faceEmbeding.endingControlStep'] || facialReferenceDefaultValues['faceEmbeding.endingControlStep']
            ),
            pixelPerfect: pixelPerfect,
            pixelErfect: pixelPerfect,
            preprocessor: 60,
            model: 1128823,
            sourceImageId: tempSourceImagePath,
          },
          {
            allowPreview: 1,
            unitOrder: ++index,
            annotationParameters: { instantIdFaceKeypoints: { preprocessorResolution } },
            controlMode: 0,
            controlWeight: +(
              item['faceKeypoints.controlWeight'] || facialReferenceDefaultValues['faceKeypoints.controlWeight']
            ),
            resizeMode: +(item['faceKeypoints.resizeMode'] || facialReferenceDefaultValues['faceKeypoints.resizeMode']),
            startingControlStep: +(
              item['faceKeypoints.startingControlStep'] ||
              facialReferenceDefaultValues['faceKeypoints.startingControlStep']
            ),
            endingControlStep: +(
              item['faceKeypoints.endingControlStep'] || facialReferenceDefaultValues['faceKeypoints.endingControlStep']
            ),
            pixelPerfect: pixelPerfect,
            pixelErfect: pixelPerfect,
            preprocessor: 59,
            model: 1128809,
            width: referenceImageSize.width,
            height: referenceImageSize.height,
            sourceImageId: tempReferenceImagePath,
          },
        );
        continue;
      }

      const cnOptions = {
        allowPreview: 1,
        unitOrder: ++index,
        annotationParameters: annotationParameters,
        controlWeight: item.controlWeight,
        controlMode: +item.controlMode,
        startingControlStep: +item.startingControlStep,
        endingControlStep: +item.endingControlStep,
        pixelPerfect: pixelPerfect,
        pixelErfect: pixelPerfect,
        resizeMode: typeof item.resizeMode !== 'undefined' ? Number(item.resizeMode) : 1,
        preprocessor: +preprocessor,
        model: model ? +model : 0,
        sourceImageId: tempSourceImagePath,
        imagePosition: item.imagePosition,
      };

      if (item.maskImageId) {
        cnOptions.maskImageId = item.maskImageId;
      }

      baseParams.controlNet.push(cnOptions);
    }
  }

  const enabledAddon = isSD3FamilyModel || isSimpleMode ? [] : params.addOns?.filter((item) => item.value !== null);

  // 处理addons
  if (enabledAddon?.length) {
    baseParams.adetailerEnable = 1;

    baseParams.adetailerList = enabledAddon.map((item) => ({
      adetailerModelVerId: item.value || 0,
      prompt: '',
      negativePrompt: '',
      detection: {
        threshold: 0.3,
        maskMinAreaRatio: 0,
        maskMaxAreaRatio: 1,
      },
      maskPreprocessing: {
        maskXOffset: 0,
        maskYOffset: 0,
        maskScaling: item.edge,
        maskMergeMode: item.mergeMode || 0,
      },
      inpainting: {
        maskBlur: 4,
        denoisingStrength: 0.4,
        onlyMasked: true,
        onlyMaskedPaddingPixels: 32,
        separateWH: false,
        separateSteps: true,
        adetailerSteps: 25,
        separateCFGScale: false,
        separateNoiseMultiplier: false,
        restoreFacesAfterADetailer: false,
      },
    }));
  }

  // 最后统一尝试修正一下尺寸为8或者64的倍数
  // 这是个比较高风险的处理哦，因为有可能会导致生成结果和预期不一致

  const MOD_UNIT = 8; // 后来inpaint也由64改为8了
  const restWidth = generateParams.width % MOD_UNIT;
  const restHeight = generateParams.height % MOD_UNIT;

  if (restWidth !== 0) {
    generateParams.width = generateParams.width - restWidth + MOD_UNIT;
  }

  if (restHeight !== 0) {
    generateParams.height = generateParams.height - restHeight + MOD_UNIT;
  }

  if (withFaceCN && isSimpleMode) {
    generateParams.cfgScale = 2.5;
  }

  // 高清放大最新预设参数，对照新版webui

  if (isFluxModel || isSD35Model) {
    baseParams.generateType = 22;
    baseParams.coreParamsField = 'img2img';
    // 2024.12.6 提出规则修改
    generateParams.denoisingStrength = params.strength * 0.001 + 0.9;
    generateParams.sourceImageId = tempReferenceImagePath;
    generateParams.maskImageId = maskImageId;
    baseParams.img2img = mapSD3Params(generateParams);
    delete baseParams.controlNet;
  } else {
    baseParams.generateType = 1;
    baseParams.coreParamsField = 'text2img';
    baseParams.text2img = generateParams;
  }

  delete baseParams.projectData.controlNet;

  baseParams.projectData.samplerMethodName = samplerMethodOptions.find(
    (item) => item.value === generateParams.samplingMethod,
  )?.label;

  return baseParams;
};

export const inpaintResultHandler = async (taskData, results) => {
  // 如果要做一些别的操作，可以在这里处理
  // ...

  // 通用的衍生生图结果处理方式
  return applyDerivativeResults('inpaint', taskData, results);
};
